import { useEffect, useRef } from "react";
import { getImageUrl } from "../helper/image";
import DeferredImage from "./imgs/DeferredImage";
import "../styles/recentCarousel.css";
import ProductName from "./ProductName";

const DRAG_THRESHOLD = 8;
const TAP_THRESHOLD = 10;

const VARIANT_PREFIX = {
  menu: "menu-recent",
  home: "home-recent",
};

function RecentThumb({ item, className }) {
  return (
    <DeferredImage
      className={`recent-carousel-thumb ${className}`}
      src={getImageUrl(item, false)}
      alt=""
      loading="lazy"
    />
  );
}

function RecentCard({ item, itemClassName, thumbClassName, nameClassName, onSelect }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={`recent-carousel-item ${itemClassName}`}
      data-recent-id={item.id}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(item);
        }
      }}
    >
      <RecentThumb item={item} className={thumbClassName} />
      <span className={`recent-carousel-name ${nameClassName}`}>
        <ProductName name={item.name} type={item.type} useFullName stacked />
      </span>
    </div>
  );
}

function wrapOffset(offset, halfWidth) {
  if (!halfWidth) return offset;
  let next = offset;
  while (next <= -halfWidth) next += halfWidth;
  while (next > 0) next -= halfWidth;
  return next;
}

function useRecentCarousel(
  viewportRef,
  trackRef,
  itemCount,
  scrollSeconds,
  variant = "menu",
  items = [],
  onSelect
) {
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const itemsByIdRef = useRef(new Map());
  const dragRef = useRef({
    pending: false,
    active: false,
    startX: 0,
    startY: 0,
    startOffset: 0,
    moved: false,
    pointerId: null,
  });

  useEffect(() => {
    itemsByIdRef.current = new Map(items.map((item) => [item.id, item]));
  }, [items]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || itemCount === 0) return;

    const measure = () => {
      halfWidthRef.current = track.scrollWidth / 2;
      offsetRef.current = wrapOffset(offsetRef.current, halfWidthRef.current);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);

    let frameId = 0;
    let lastTime = 0;

    const tick = (time) => {
      const halfWidth = halfWidthRef.current;
      const dt = lastTime ? (time - lastTime) / 1000 : 0;
      lastTime = time;

      if (
        !dragRef.current.pending &&
        !dragRef.current.active &&
        halfWidth > 0
      ) {
        const pxPerSecond = halfWidth / scrollSeconds;
        offsetRef.current = wrapOffset(
          offsetRef.current - pxPerSecond * dt,
          halfWidth
        );
      }

      track.style.transform = `translateX(${offsetRef.current}px)`;

      const bounds = viewport.getBoundingClientRect();
      const centerX = bounds.left + bounds.width / 2;
      const viewportHalf = bounds.width / 2;

      viewport.querySelectorAll(".recent-carousel-tilt").forEach((el) => {
        const rect = el.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const dist = Math.max(-1, Math.min(1, (cardCenter - centerX) / viewportHalf));
        const abs = Math.abs(dist);

        const isHome = variant === "home";
        const scale = (isHome ? 1.32 : 1.14) - abs * (isHome ? 0.48 : 0.38);
        const opacity = 1 - abs * (isHome ? 0.38 : 0.5);
        const rotateY = dist * (isHome ? -40 : -32);
        const translateZ = (1 - abs) * (isHome ? 64 : 36);

        el.style.transform = `perspective(900px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`;
        el.style.opacity = String(opacity);
        el.style.zIndex = String(Math.round((1 - abs) * 100));
      });

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    const applyDragOffset = (clientX) => {
      const halfWidth = halfWidthRef.current;
      const drag = dragRef.current;
      const dx = clientX - drag.startX;
      offsetRef.current = wrapOffset(drag.startOffset + dx, halfWidth);
    };

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      dragRef.current = {
        pending: true,
        active: false,
        startX: event.clientX,
        startY: event.clientY,
        startOffset: offsetRef.current,
        moved: false,
        pointerId: event.pointerId,
      };
    };

    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if ((!drag.pending && !drag.active) || drag.pointerId !== event.pointerId) {
        return;
      }

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!drag.moved && (absX > DRAG_THRESHOLD || absY > DRAG_THRESHOLD)) {
        drag.moved = true;
        if (absX >= absY) {
          drag.active = true;
          drag.pending = false;
          viewport.classList.add("is-dragging");
          viewport.setPointerCapture(event.pointerId);
        }
      }

      if (drag.active) {
        applyDragOffset(event.clientX);
      }
    };

    const endDrag = (event) => {
      const drag = dragRef.current;
      if ((!drag.pending && !drag.active) || drag.pointerId !== event.pointerId) {
        return;
      }

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const isTap =
        Math.abs(dx) <= TAP_THRESHOLD && Math.abs(dy) <= TAP_THRESHOLD;

      if (isTap && onSelect) {
        const card = event.target.closest("[data-recent-id]");
        if (card) {
          const item = itemsByIdRef.current.get(Number(card.dataset.recentId));
          if (item) {
            onSelect(item);
          }
        }
      }

      if (drag.active && event?.pointerId != null) {
        try {
          viewport.releasePointerCapture(event.pointerId);
        } catch {
          // ignore if capture was already released
        }
      }

      dragRef.current = {
        pending: false,
        active: false,
        startX: 0,
        startY: 0,
        startOffset: 0,
        moved: false,
        pointerId: null,
      };
      viewport.classList.remove("is-dragging");
    };

    viewport.addEventListener("pointerdown", onPointerDown, { capture: true });
    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", endDrag);
    viewport.addEventListener("pointercancel", endDrag);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      viewport.removeEventListener("pointerdown", onPointerDown, { capture: true });
      viewport.removeEventListener("pointermove", onPointerMove);
      viewport.removeEventListener("pointerup", endDrag);
      viewport.removeEventListener("pointercancel", endDrag);
      viewport.classList.remove("is-dragging");
    };
  }, [viewportRef, trackRef, itemCount, scrollSeconds, variant, onSelect]);

  return null;
}

const RecentlyViewedCarousel = ({
  items,
  onSelect,
  variant = "menu",
  title = null,
  subtitle = null,
}) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const loopItems = items?.length ? [...items, ...items] : [];
  const scrollSeconds = Math.max(28, items.length * 5);
  const prefix = VARIANT_PREFIX[variant] ?? VARIANT_PREFIX.menu;

  useRecentCarousel(
    viewportRef,
    trackRef,
    loopItems.length,
    scrollSeconds,
    variant,
    items,
    onSelect
  );

  if (!items?.length) {
    return null;
  }

  const carousel = (
    <div className={`${prefix}-widget`}>
      {variant === "menu" && title && (
        <p className={`${prefix}-label white`}>{title}</p>
      )}
      <div
        className={`recent-carousel-viewport ${prefix}-viewport`}
        ref={viewportRef}
      >
        <div className={`recent-carousel-track ${prefix}-track`} ref={trackRef}>
          {loopItems.map((item, index) => (
            <div className="recent-carousel-tilt" key={`${item.id}-${index}`}>
              <RecentCard
                item={item}
                onSelect={onSelect}
                itemClassName={`${prefix}-item`}
                thumbClassName={`${prefix}-thumb`}
                nameClassName={`${prefix}-name`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (variant === "home") {
    return (
      <section className="home-recent-section">
        {(title || subtitle) && (
          <div className="home-recent-heading">
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </div>
        )}
        {carousel}
      </section>
    );
  }

  return carousel;
};

export default RecentlyViewedCarousel;
